import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Particles from "react-tsparticles";
import type { Engine } from "tsparticles-engine";
import { loadFull } from "tsparticles";
import { triggerFloatingWordClick } from "@/components/easteregg/ClickStormEasterEgg.tsx";
import { readFlyingTextEnabled, subscribeFlyingTextEnabled } from "@/lib/flyingTextEnabled";

export type StarryScope = "full" | "workspace";

// Layering
const Z_PARTICLES = -20;
const Z_OVERLAY = -15;
const Z_PROMPTS = 1;
const Z_WORKSPACE_BG = 0;

const CHAT_INPUT_SELECTOR = "textarea.resize-none";

export const PROMPTS = [
  "Has the gas been coordinated on {project}?",
  "Has the flow test been received for {project}?",
  "Has the fixture count been finalized for {project}?",
  "What percentage is Plumbing for {project} currently at?",
  "Has a 50% site plan been sent to civil for {project}?",
  "Have cooling loads been calculated for {project}?",
  "Has the lighting comcheck been performed for {project}?",
  "Have the site photometrics been completed for {project}?",
  "Have roof drain chases been coordinated for {project}?",
];

const particlesOptionsBase = {
  fpsLimit: 60,
  background: { color: { value: "transparent" } },
  particles: {
    number: {
      value: 90,
      density: { enable: true, area: 1400 },
    },
    color: {
      value: ["#ffffff", "#c084fc", "#a855f7"],
    },
    opacity: {
      value: { min: 0.4, max: 1 },
    },
    size: {
      value: { min: 0.6, max: 1.6 },
    },
    move: {
      enable: true,
      speed: 0.15,
      random: true,
      straight: false,
      outModes: {
        default: "out" as const,
      },
    },
  },
  interactivity: {
    events: {
      onHover: {
        enable: true,
        mode: "repulse",
        parallax: { enable: true, force: 6, smooth: 20 },
      },
      resize: true,
    },
    modes: {
      repulse: { distance: 40, duration: 0.4 },
    },
  },
  detectRetina: true,
};

function FloatingPrompts({
  fontFamily,
  baseFontPx,
  dimmed,
  position,
  interactive,
}: {
  fontFamily: string;
  baseFontPx: number;
  dimmed: boolean;
  position: "fixed" | "absolute";
  interactive: boolean;
}) {
  const [items, setItems] = useState<any[]>([]);
  const itemsRef = useRef<any[]>([]);
  useEffect(() => { itemsRef.current = items; }, [items]);

  const measureCtxRef = useRef<CanvasRenderingContext2D | null>(null);
  const NUM_ITEMS = 4;
  const MARGIN = 72;
  const [hoveredId, setHoveredId] = useState<number | null>(null);

  useEffect(() => {
    const canvas = document.createElement("canvas");
    measureCtxRef.current = canvas.getContext("2d");
    return () => { measureCtxRef.current = null; };
  }, []);

  const measureTextWidth = (text: string, sizePx: number) => {
    const ctx = measureCtxRef.current;
    if (!ctx) return text.length * sizePx * 0.6;
    ctx.font = `700 ${sizePx}px ${fontFamily}`;
    return ctx.measureText(text).width;
  };

  const insertIntoChatInput = (text: string) => {
    const el = document.querySelector<HTMLTextAreaElement>(CHAT_INPUT_SELECTOR);
    if (!el) return;
    el.value = text;
    el.dispatchEvent(new Event("input", { bubbles: true }));
    el.focus();
  };

  const spawnItem = (id: number) => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const edge = Math.floor(Math.random() * 4);
    const size = Math.max(baseFontPx * 1.1, 16);
    const text = PROMPTS[Math.floor(Math.random() * PROMPTS.length)];
    const width = measureTextWidth(text, size);
    let x = 0, y = 0;

    if (edge === 0) { x = -MARGIN - width; y = Math.random() * h; }
    if (edge === 1) { x = w + MARGIN; y = Math.random() * h; }
    if (edge === 2) { x = Math.random() * w; y = -MARGIN; }
    if (edge === 3) { x = Math.random() * w; y = h + MARGIN; }

    const angle = Math.atan2(h / 2 - y, w / 2 - x);
    const speed = 120;

    return {
      id,
      text,
      x,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      size,
      width,
    };
  };

  useEffect(() => {
    setItems(Array.from({ length: NUM_ITEMS }, (_, i) => spawnItem(i)));
  }, [fontFamily, baseFontPx]);

  useEffect(() => {
    let raf = 0;
    let last = performance.now();

    const tick = (now: number) => {
      const dt = (now - last) / 1000;
      last = now;
      setItems((prev) =>
        prev.map((it) => {
          const nx = it.x + it.vx * dt;
          const ny = it.y + it.vy * dt;
          if (
            nx > window.innerWidth + MARGIN ||
            nx + it.width < -MARGIN ||
            ny > window.innerHeight + MARGIN ||
            ny < -MARGIN
          ) {
            return spawnItem(it.id);
          }
          return { ...it, x: nx, y: ny };
        })
      );
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div
      style={{
        position,
        inset: 0,
        zIndex: Z_PROMPTS,
        pointerEvents: "none",
      }}
    >
      {items.map((it) => (
        <span
          key={it.id}
          onMouseEnter={() => setHoveredId(it.id)}
          onMouseLeave={() => setHoveredId(null)}
          onMouseDown={
            interactive
              ? (e) => {
                  e.preventDefault();
                  insertIntoChatInput(it.text);
                  triggerFloatingWordClick();
                }
              : undefined
          }
          style={{
            position: "absolute",
            transform: `translate(${it.x}px, ${it.y}px)`,
            fontFamily,
            fontSize: `${it.size}px`,
            fontWeight: 500,
            color: "hsl(var(--accent))",
            opacity: dimmed ? 0.2 : hoveredId === it.id ? 1 : 0.9,
            textShadow: dimmed
              ? "none"
              : "0 0 6px hsl(var(--accent)), 0 0 12px hsl(var(--accent))",
            pointerEvents: interactive ? "auto" : "none",
            cursor: interactive ? "pointer" : "default",
            userSelect: "none",
          }}
        >
          {it.text}
        </span>
      ))}
    </div>
  );
}

export default memo(function StarryBackground({
  variant = "full",
  showParticles = true,
  showPrompts = true,
}: {
  variant?: StarryScope;
  showParticles?: boolean;
  showPrompts?: boolean;
}) {
  const [ready, setReady] = useState(false);
  const [fontFamily, setFontFamily] = useState("Inter, system-ui");
  const [fontPx, setFontPx] = useState(16);
  const [inputFocused, setInputFocused] = useState(false);
  const [flyingTextEnabled, setFlyingTextEnabled] = useState(() =>
    readFlyingTextEnabled({ defaultValue: true }),
  );

  const effectiveShowPrompts = useMemo(
    () => Boolean(showPrompts) && flyingTextEnabled,
    [showPrompts, flyingTextEnabled],
  );

  useEffect(() => {
    if (document.readyState === "complete") setReady(true);
    else window.addEventListener("load", () => setReady(true), { once: true });
  }, []);

  useEffect(() => {
    // Sync with header toggle (same-tab) and storage changes (cross-tab).
    return subscribeFlyingTextEnabled((enabled) => setFlyingTextEnabled(Boolean(enabled)));
  }, []);

  useEffect(() => {
    const onFocus = (e: FocusEvent) => {
      const t = e.target as HTMLElement;
      if (t?.matches?.(CHAT_INPUT_SELECTOR)) setInputFocused(true);
    };
    const onBlur = (e: FocusEvent) => {
      const t = e.target as HTMLElement;
      if (t?.matches?.(CHAT_INPUT_SELECTOR)) setInputFocused(false);
    };
    document.addEventListener("focusin", onFocus, true);
    document.addEventListener("focusout", onBlur, true);
    return () => {
      document.removeEventListener("focusin", onFocus, true);
      document.removeEventListener("focusout", onBlur, true);
    };
  }, []);

  const particlesInit = useCallback(async (engine: Engine) => {
    await loadFull(engine);
  }, []);

  if (!ready) return null;

  return (
    <>
      {showParticles && (
        <>
          <Particles
            init={particlesInit}
            options={{
              ...particlesOptionsBase,
              fullScreen: {
                enable: variant === "full",
                zIndex: Z_PARTICLES,
              },
            }}
            style={{
              position: variant === "full" ? "fixed" : "absolute",
              inset: 0,
              mixBlendMode: "screen",
            }}
          />

          {/* Contrast-preserving darkening (NO fog) */}
          <div
            aria-hidden
            style={{
              position: variant === "full" ? "fixed" : "absolute",
              inset: 0,
              zIndex: Z_OVERLAY,
              pointerEvents: "none",
              background:
                "radial-gradient(circle at center, rgba(0,0,0,0.1), rgba(0,0,0,0.8))",
            }}
          />
        </>
      )}

      {/* {effectiveShowPrompts && (
        <FloatingPrompts
          fontFamily={fontFamily}
          baseFontPx={fontPx}
          dimmed={inputFocused}
          position={variant === "full" ? "fixed" : "absolute"}
          interactive={variant === "full"}
        />
      )} */}
    </>
  );
});
